import { useMemo } from 'react'
import './PlayerCashInvestments.scss'
import { useSelector } from '../../../../hooks/useSelector'
import LOCRateUpgradesPopup from './LOCRateUpgradesPopup'
import LOCMaxUpgradesPopup from './LOCMaxUpgradesPopup'
import BondRateUpgradesPopup from './BondRateUpgradesPopup'
import BondMaxUpgradesPopup from './BondMaxUpgradesPopup'
import usePlayerActions from '../../hooks/usePlayerActions'
import numeral from 'numeral'
import { round } from 'lodash'
import { createBondUpgradeLevels, createLocUpgradeLevels, truncateCash } from '@prometheus/utils'
import { calcQuest } from './calculateQuestBonus'
import classNames from 'classnames'
import { Card, Stack, Typography } from '@prometheus/components'
import PromiseButtonV2 from '@/components/PromiseButtonV2'

export default () => {
  const loc = useSelector(s => s.game.sheet?.loc)
  const bonds = useSelector(s => s.game.sheet?.bonds)
  const options = useSelector(s => s.game.options)
  const is_map = useSelector(s => s.game.galaxy_display)
  const prime = useSelector(s => s.game.metadata?.prime_rate) || 0
  const risk_adjusted_rate = useSelector(s => s.game.sheet?.credit_risk_adjusted_rate) || 0
  const global_bond_rate_addition = useSelector(s => s.game.metadata?.global_bond_rate_addition) || 0
  const quest_bond_bonus = useSelector(s => s.game.sheet?.quests.rewards.bond_rate_bonus)
  const quest_loc_bonus = useSelector(s => s.game.sheet?.quests.rewards.loc_rate_bonus)

  const quest_loc_bonus_total = calcQuest(quest_loc_bonus || [])
  const quest_bond_bonus_total = calcQuest(quest_bond_bonus || [])
  const bond_rate_upgrades = useMemo(() => createBondUpgradeLevels(options?.bond_rate_upgrades ?? []), [options?.bond_rate_upgrades])
  const loc_max_upgrades = useMemo(() => createLocUpgradeLevels(options?.loc_max_upgrades ?? []), [options?.loc_max_upgrades])

  const {
    onModifyCashAccounts,
  } = usePlayerActions()

  if (!loc || !bonds || !bond_rate_upgrades.length || !loc_max_upgrades.length) {
    return null
  }

  const bond_max = (options?.bond_max_upgrades[bonds.max_level].max || 0) + bonds.max_bonuses
  const bond_rate = (bond_rate_upgrades[bonds.rate_level].rate) + (prime * .5 + global_bond_rate_addition + quest_bond_bonus_total) * 100
  const loc_max = (loc_max_upgrades[loc.max_level].max || 0) + loc.max_bonuses
  const loc_rate = (options?.loc_rate_upgrades[loc.rate_level].rate || 0) + (prime + risk_adjusted_rate + quest_loc_bonus_total) * 100

  return (
    <>
      <Card transparent className={classNames({ is_map })} padding="sm" style={{ position: 'relative', borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRightWidth: 0, zIndex: 1 }}>
        <Stack>
          <Typography variant="h4" retro color="primary" uppercase>Cash Accounts</Typography>
          <Card padding="sm">
            <Stack>
              <Stack direction="row" style={{ alignItems: 'center' }}>
                <Typography variant="h5" retro>BONDS</Typography>
                <PromiseButtonV2 ignoreGameLogic size="xs" outline variant="danger" onClick={() => onModifyCashAccounts('withdraw_bonds')}>Sell</PromiseButtonV2>
                <PromiseButtonV2 ignoreGameLogic size="xs" outline variant="primary" onClick={() => onModifyCashAccounts('deposit_bonds')}>Buy</PromiseButtonV2>
              </Stack>
              <Stack space="md" direction="row">
                <Typography variant="h4" color={bonds.cash > 0 ? 'primary' : undefined}>{truncateCash(bonds.cash, {}, true)}</Typography>
                <Typography variant="h4">/</Typography>
                <BondMaxUpgradesPopup>
                  <Card padding="none" interactive style={{ padding: '1px 4px' }}>
                    <Typography variant="h4">{truncateCash(bond_max, {}, true)}</Typography>
                  </Card>
                </BondMaxUpgradesPopup>
                <Typography variant="h4">@</Typography>
                <BondRateUpgradesPopup>
                  <Card padding="none" interactive style={{ padding: '1px 4px' }}>
                    <Typography variant="h4">{bond_rate.toFixed(1)}%</Typography>
                  </Card>
                </BondRateUpgradesPopup>
              </Stack>
            </Stack>
          </Card>
          <Card padding="sm">
            <Stack>
              <Stack direction="row" style={{ alignItems: 'center' }}>
                <Typography variant="h5" retro>LOC</Typography>
                <PromiseButtonV2 ignoreGameLogic size="xs" outline variant="danger" onClick={() => onModifyCashAccounts('withdraw_loc')}>Withdraw 25%</PromiseButtonV2>
                <PromiseButtonV2 ignoreGameLogic size="xs" outline variant="primary" onClick={() => onModifyCashAccounts('deposit_loc')}>Deposit</PromiseButtonV2>
              </Stack>
              <Stack space="md" direction="row">
                <Typography variant="h4" color={loc.cash > 0 ? 'danger' : undefined}>{investmentTruncate(loc.cash, {}, true)}</Typography>
                <Typography variant="h4">/</Typography>
                <LOCMaxUpgradesPopup>
                  <Card padding="none" interactive style={{ padding: '1px 4px' }}>
                    <Typography variant="h4">{investmentTruncate(loc_max, {}, true)}</Typography>
                  </Card>
                </LOCMaxUpgradesPopup>
                <Typography variant="h4">@</Typography>
                <LOCRateUpgradesPopup>
                  <Card padding="none" interactive style={{ padding: '1px 4px' }}>
                    <Typography variant="h4">{loc_rate.toFixed(1)}%</Typography>
                  </Card>
                </LOCRateUpgradesPopup>
              </Stack>
            </Stack>
          </Card>
        </Stack>
      </Card>
    </>
  )
}

export const investmentTruncate = (currency: number, options?: any, pennies?: boolean) => {
  let format = (options && options.raw) ? '' : '$'
  let is_negative = ''
  if (currency < 0) {
    currency *= -1
    is_negative = '-'
  }

  if (currency < 10000 && currency > -10000) {
    format += '0'
  } else if ([7, 10, 13, 16, 19].indexOf(currency.toString().length) !== -1) {
    format += '0.0a'
  } else {
    format += '0a'
  }

  if (pennies) {
    format += '.00'
  }

  if (options && options.removeDigits && options.raw) {
    format = (options && options.raw) ? '' : '$'
    if ([4, 7, 10, 13, 17].indexOf(currency.toString().length) !== -1) {
      format += '0.0a'
    } else {
      format += '0a'
    }
  }

  if (currency > 1000000000) {
    const value = Math.round(currency)
    let new_value = 0
    let more_digits = 0
    let more_digits_text = ''
    let final_string = ''
    const abbreviations = ['k', 'm', 'b', 't', 'q', 'Q', 's', 'S', 'o', 'n', 'd', 'u', 'D', 'T', 'q', 'Q', 's', 'S', 'O', 'N', 'V', '?']
    const x = 3
    for (let i = 1; i < abbreviations.length; i++) {
      const y = x * i
      let total_digits = value.toString().length
      if (value.toString().includes('e')) { // returns an array with the two values meaning it contains an e
        const scientific_notation = value.toString().split('e')
        let prefix_number = parseFloat(scientific_notation[0])
        total_digits = Math.floor(parseFloat(scientific_notation[1]) / 3)
        const suffix = abbreviations[Math.min(total_digits - 1, abbreviations.length - 1)]
        const decimals_off = parseFloat(scientific_notation[1]) - total_digits * 3
        prefix_number *= 10 ** decimals_off
        if (prefix_number >= 10) {
          prefix_number = Math.round(prefix_number)
        } else {
          prefix_number = round(prefix_number, 1)
        }
        final_string = is_negative + prefix_number + suffix
        if (!options?.raw) {
          final_string = is_negative + '$' + final_string
        }
        return final_string
      }
      if (y > total_digits - 1) {
        new_value = parseFloat(value.toString().substring(0, value.toString().length - y + 3))
        if (new_value.toString().length < 2) { // if we rounded a lot, make it a little bigger
          more_digits = parseFloat(value.toString().substring(1, 2))
          more_digits_text += '.'
          more_digits_text += more_digits.toString()
        }
        final_string = is_negative + `${new_value}${more_digits_text}${abbreviations[i - 2]}`
        if (!options?.raw) {
          final_string = is_negative + '$' + final_string
        }
        return final_string
      }
    }
    return final_string
  }

  return is_negative + numeral(currency).format(format)
}
